import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ProtectedRoute from './ProtectedRoute';
import Layout from '../components/layout/Layout';
import { EmployeeRole } from '../models/types';
import BatchProcessDetails from '../components/processing/BatchProcessDetails';

// Lazy load components for better performance
const Login = React.lazy(() => import('../components/auth/Login'));
const Processing = React.lazy(() => import('../components/processing/Processing'));
const Materials = React.lazy(() => import('../components/processing/Materials'));
const CreateBatch = React.lazy(() => import('../components/processing/CreateBatch'));
const BatchDetails = React.lazy(() => import('../components/processing/BatchDetails'));
const UpdateBatch = React.lazy(() => import('../components/processing/UpdateBatch'));
const BatchDetailPage = React.lazy(() => import('../components/supervisorPages/BatchDetailPage'));
const TaskAssignment = React.lazy(() => import('../components/processing/TaskAssignment'));
const Analytics = React.lazy(() => import('../components/analytics/Analytics'));
const WorkerTasks = React.lazy(() => import('../components/worker/WorkerTasks'));
const ViewTaskDetails = React.lazy(() => import('../components/worker/ViewTaskDetails'));

const NotFound = React.lazy(() => import('../components/common/NotFound'));

const AppRouter: React.FC = () => {
  const { isAuthenticated, isLoading, getCurrentUser, selectedRole } = useAuthStore();

  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    if (token && !isAuthenticated) {
      getCurrentUser();
    }
  }, [isAuthenticated, getCurrentUser]);

  if (isLoading) {
    return <LoadingSpinner size="large" text="Initializing application..." />;
  }

  return (
    <Router>
      <Suspense fallback={<LoadingSpinner size="large" text="Loading..." />}>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                // Redirect based on selectedRole if already authenticated
                <Navigate
                  to={
                    selectedRole === EmployeeRole.Admin
                      ? '/admin/processing'
                      : selectedRole === EmployeeRole.LineSupervisor
                        ? '/supervisor/processing'
                        : '/worker/tasks'
                  }
                  replace
                />
              ) : (
                <Login />
              )
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={[EmployeeRole.Admin]}>
                <Layout>
                  <Routes>
                    <Route path="processing" element={<Processing />} />
                    <Route path="materials" element={<Materials />} />
                    <Route path="create-batch" element={<CreateBatch />} />
                    <Route path="batch-details/:batchKey" element={<BatchDetails />} />
                    <Route path="edit-batch/:batchKey" element={<UpdateBatch />} />
                    <Route path="assign-task" element={<TaskAssignment />} />
                    <Route path="batch-process-details" element={<BatchProcessDetails />} />
                    <Route path="*" element={<Navigate to="processing" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Supervisor routes */}
          <Route
            path="/supervisor/*"
            element={
              <ProtectedRoute allowedRoles={[EmployeeRole.LineSupervisor]}>
                <Layout>
                  <Routes>
                    <Route path="analytics" element={<Analytics />} />
                    <Route path="processing" element={<Analytics />} />
                    <Route path="batches/:batchkey" element={<BatchDetailPage />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Worker routes */}
          <Route
            path="/worker/*"
            element={
              <ProtectedRoute
                allowedRoles={[
                  EmployeeRole.MachineOperator,
                  EmployeeRole.CleaningWorker,
                  EmployeeRole.NichromeOperators,
                  EmployeeRole.PackagingWorker,
                ]}
              >
                <Layout>
                  <Routes>
                    <Route path="tasks" element={<WorkerTasks />} />
                    <Route path="task-details/:key" element={<ViewTaskDetails />} />
                    <Route path="*" element={<Navigate to="tasks" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Root path redirects based on role */}
          <Route
            path="/"
            element={
              selectedRole ? (
                <Navigate
                  to={
                    selectedRole === EmployeeRole.Admin
                      ? '/admin/processing'
                      : selectedRole === EmployeeRole.LineSupervisor
                        ? '/supervisor/processing'
                        : '/worker/tasks'
                  }
                  replace
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* 404 route */}
          <Route path="*" element={<NotFound />} />
          {/* <Route path="/batches/:batchkey" element={<BatchDetailPage />} /> */}
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRouter; 